<app-cw-new-card
  [showBody]="true"
  [showHeader]="widgetType !== widgetConstants.COMPACT"
  [showShadow]="widgetType !== widgetConstants.COMPACT"
  [customCardClass]="customCSS"
>
  <div header>
    <app-cw-card-header
      [title]="templateTitle | translation: templateContent.TEMPLATE_CONTENT: lang"
      [subtitle]="templateSubTitle | translation: templateContent.TEMPLATE_CONTENT: lang"
    />
  </div>

  <!-- tabindex -1: the last place focus returns to when a payment lock ends (focusAfterPaymentLock). -->
  <div body class="cw-payment-pane" tabindex="-1" [ngClass]="{ 'my-4': widgetType !== widgetConstants.COMPACT }">
    @if (showPaymentSuccessMsg) {
      <app-alert
        title="{{ 'summary_page_translations.payment_success_title' | translate }}"
        [text]="paymentSuccessMsg"
        type="success"
      />
    }

    @if (showPaymentFailedMsg) {
      <app-alert [text]="paymentFailedMsg | translate" type="danger" />
    }

    <!-- A paid booking whose answer was lost: it may exist, so the payment form stays down until
         the customer chooses to pay again. -->
    @if (showBookingOutcomeUnknown()) {
      <div class="alert alert-danger summary-warning" role="alert">
        {{ 'payment_components.bookingOutcomeUnknownError' | translate }}
        <button type="button" class="btn btn-link alert-link p-0" (click)="payAgain()">
          {{ 'payment_components.pay_again' | translate }}
        </button>
      </div>
    }

    <!-- The backend will not claim the payment session again. A booking may stand behind it, so
         the customer checks first and re-arms the form with "Pay again". Polite: the notice beside
         it is already announced. -->
    @if (showPaymentSessionSpent()) {
      <app-alert alertRole="status" type="info" [text]="'payment_components.paymentSessionSpentError' | translate">
        <button type="button" class="btn btn-link alert-link p-0" (click)="payAgain()">
          {{ 'payment_components.pay_again' | translate }}
        </button>
      </app-alert>
    }

    <!-- A paid booking still held by the booking lock after every resend: it is most likely being
         completed by this payment's own first request. No "Pay again" - never a second payment. -->
    @if (showBookingStillCompleting()) {
      <app-alert alertRole="status" type="info" [text]="'payment_components.bookingStillCompletingError' | translate" />
    }

    @if (!hidePaymentOptions) {
      <div class="ms-2 w-50">
        @if (isOnlinePaymentMandatory && paymentType === paymentTypes.STORE && priceAfterCouponReduction > 0) {
          <app-alert text="{{ 'summary_page_translations.paymentMandatory' | translate }}" type="warning" />
        }
      </div>

      @if (price > 0) {
        <div class="d-flex flex-column align-items-start cw-stripe-box">
          <div class="stripe-title payment-type cbcw-payment-selection-label cw-payment-pane-heading" tabindex="-1">
            {{ 'summary_page_translations.payment_type' | translate }}:
          </div>
          <div class="stripe-field mt-2">
            @if (useCardLayout) {
              <div
                class="cw-payment-method-cards"
                [attr.role]="'radiogroup'"
                [attr.aria-label]="'summary_page_translations.payment_type' | translate"
              >
                @for (option of paymentOptions; track option.value) {
                  <div
                    class="cw-payment-method-card"
                    [hidden]="isPaymentOptionHidden(option.value)"
                    [ngClass]="{ 'cw-payment-method-card--selected': paymentType === option.value }"
                  >
                    <label class="cw-payment-method-card-header">
                      <input
                        (change)="onSelectPaymentType(option.value)"
                        [checked]="paymentType === option.value"
                        [disabled]="adyenPaymentInFlight()"
                        [value]="option.value"
                        class="cw-payment-method-card-radio"
                        name="paymentType"
                        type="radio"
                      />
                      <span class="cw-payment-method-card-label">{{ option.label }}</span>
                    </label>

                    @if (paymentType === option.value) {
                      <ng-container [ngTemplateOutlet]="providerBlocks" />
                    }
                  </div>
                }
              </div>

              @if (!showAllPaymentOptions && paymentOptions.length > 1) {
                <div class="mt-2">
                  <a (click)="displayAllPaymentOptions()" class="skip" href="javascript:void(0)">
                    {{ 'summary_page_translations.other_payment_options' | translate }}
                  </a>
                </div>
              }
            } @else if (paymentOptions.length > 0) {
              <ng-select
                (ngModelChange)="onSelectPaymentType($event)"
                [(ngModel)]="paymentType"
                [items]="paymentOptions"
                bindValue="value"
                bindLabel="label"
                [clearable]="false"
                [multiple]="false"
                [placeholder]="'summary_page_translations.choose_payment_method' | translate"
                [searchable]="false"
                [readonly]="adyenPaymentInFlight()"
                class="payment-type-select"
                name="paymentType"
                [required]="true"
              ></ng-select>
            }

            <!-- Adyen and Saferpay get their own "complete the payment first" message below. -->
            @if (showPaymentTypeError && paymentType !== paymentTypes.ADYEN && paymentType !== paymentTypes.SAFERPAY) {
              <div class="alert alert-danger summary-warning" #paymentTypeErrorRef>
                @if (paymentType === paymentTypes.PAYPAL) {
                  {{ 'summary_page_translations.select_payment_method' | translate }}
                } @else {
                  {{ 'summary_page_translations.payment_method_required' | translate }}
                }
              </div>
            }
            <!-- Adyen: only while a Drop-in waits for the customer - not while it loads, not behind
                 its own error or result, and not while a payment authorises (Book is pressed
                 during a 3DS challenge, and "pay first" would be false there). Saferpay pays in its
                 own window, so Book before it reports back gets the same neutral message. -->
            @if (
              showPaymentTypeError && isFormValid &&
              ((paymentType === paymentTypes.ADYEN && isAdyenAwaitingPayment) || paymentType === paymentTypes.SAFERPAY)
            ) {
              <div class="alert alert-danger summary-warning" role="alert" #paymentTypeErrorRef>
                {{ 'payment_components.complete_payment_first' | translate }}
              </div>
            }
            <!-- A payment that is authorising freezes the picker and the coupon; this is the way out
                 when it never reports back. -->
            <!-- Always in the DOM, so a screen reader announces the text when it appears. Outside every
                 region the host makes inert while the payment runs. -->
            <div class="cw-payment-lock-status" role="status" aria-live="polite">
              @if (paymentType === paymentTypes.ADYEN && adyenPaymentInFlight()) {
                <span class="d-block mt-2">{{ 'payment_components.payment_in_progress' | translate }}</span>
              }
            </div>
            @if (paymentType === paymentTypes.ADYEN && adyenPaymentInFlight()) {
              <div class="mt-2">
                <button type="button" class="btn btn-link p-0 skip cw-cancel-payment" (click)="cancelAdyenPayment()">
                  {{ 'payment_components.cancel_payment' | translate }}
                </button>
              </div>
            }
            @if (hasValidCoupons && !adyenPaymentInFlight()) {
              <div class="mt-2">
                <a (click)="displayCouponField()" class="skip" href="javascript:void(0)">
                  {{ widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_DISCOUNT_CODE_APPLY_TEXT, type: 'template' } | translation: templateContent.TEMPLATE_CONTENT: lang }}
                </a>
              </div>
            }
          </div>
        </div>
      }

      @if (showCouponField) {
        <!-- A coupon changes the amount the authorising session was minted for; the fieldset
             disables every control inside it until the payment reports back. -->
        <fieldset class="d-flex flex-column align-items-start cw-stripe-box mt-4 border-0 p-0 m-0" [disabled]="adyenPaymentInFlight()">
          <!-- The group's accessible name; styled exactly as the title it replaces. -->
          <legend class="stripe-title cw-coupon-legend">
            {{ widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_APPLY_COUPON_LABEL, type: 'template' } | translation: templateContent.TEMPLATE_CONTENT: lang }}:
          </legend>
          <div class="stripe-field mt-2">
            <app-coupon-form
              [partner]="partner"
              [userState]="userState"
              [cart]="cart"
              [eventCart]="eventCart"
              [widgetType]="widgetType"
              [lang]="lang"
              [widgetTemplates]="widgetTemplates"
              [preApplyCoupon]="preApplyCoupon"
              [additionalGuests]="additionalGuests"
              [anonymousGuestCount]="anonymousGuestCount"
              (couponFailedEvent)="couponFailedEvent.emit($event)"
              (couponSuccessEvent)="couponSuccessEvent.emit($event)"
            />
          </div>
        </fieldset>
      }

      @if (
        (paymentType === paymentTypes.PAYPAL ||
          paymentType === paymentTypes.SAFERPAY ||
          paymentType === paymentTypes.ADYEN) &&
        price > 0 &&
        !isFormValid
      ) {
        <div class="d-flex flex-column align-items-start cw-stripe-box mt-3">
          <app-alert class="" text="{{ 'payment_components.form_incomplete' | translate }}" type="danger" />
        </div>
      }

      @if (isExternalInvoiceEnabled && paymentType === paymentTypes.EXTERNAL_INVOICE) {
        <div class="cw-stripe-box">
          <app-cw-invoice-address-form
            [isAddressMandatory]="isInvoiceAddressMandatory"
            [lang]="lang"
            [widgetTemplates]="widgetTemplates"
            [defaultCountryCode]="partner?.countryCode"
            [prefill]="userState"
            (invoiceAddressChangeEvent)="invoiceAddressChangeEvent.emit($event)"
          />
        </div>
      }

      @if (!useCardLayout) {
        <ng-container [ngTemplateOutlet]="providerBlocks" />
      }
    }
  </div>
</app-cw-new-card>

<!-- Single source for the provider widgets: rendered inside the selected card in
     the card layout, and below the select in the dropdown layout. The conditions
     are the ones this component has always used - keep them identical in both. -->
<ng-template #providerBlocks>
  @if (isStripeEnabled && paymentType === paymentTypes.CREDITCARD && price > 0) {
    <div class="d-flex flex-column align-items-start cw-stripe-box" [class.cw-payment-method-card-body]="useCardLayout" [class.mt-4]="!useCardLayout">
      @if (!useCardLayout) {
        <div class="stripe-title">
          {{ 'summary_page_translations.creditcard' | translate }}:
        </div>
      }
      <div class="stripe-field mt-2">
        <app-stripe
          [price]="price"
          [partner]="partner"
          [userState]="userState"
          [coupon]="coupon"
          [lang]="lang"
          [finalCouponDiscount]="finalCouponDiscount"
          [originalAmount]="totalAmount"
          [priceAfterCouponReduction]="priceAfterCouponReduction"
          [connectedAccountId]="stripeConnectedAccountId"
          [storageComplete]="storageComplete"
          [addonsPrice]="customFieldsAddonsPrice"
          [widgetType]="widgetType"
          [cart]="cart"
          [eventCart]="eventCart"
          [appointmentServicesAvailable]="appointmentServicesAvailable"
          (resetStripeTokenEvent)="resetStripeTokenEvent.emit($event)"
          (stripeSuccessfulEvent)="stripeSuccessfulEvent.emit($event)"
          (stripElementLoadEvent)="stripElementLoadEvent.emit($event)"
          (storeBookingItemsEvent)="storeBookingItemsEvent.emit()"
        />
      </div>
    </div>
  }

  @if (isPaypalEnabled && paymentType === paymentTypes.PAYPAL && price > 0 && isFormValid) {
    <div
      class="cw-paypal-box"
      [class.cw-payment-method-card-body]="useCardLayout"
      [class.mt-4]="!useCardLayout"
      [ngClass]="{ 'px-0': widgetType === widgetConstants.COMPACT }"
    >
      <app-cw-paypal
        [additionalGuests]="additionalGuests"
        [anonymousGuestCount]="anonymousGuestCount"
        [paypalSetting]="paypalSetting"
        [price]="price"
        [partner]="partner"
        [appointmentServicesAvailable]="appointmentServicesAvailable"
        [cart]="cart"
        [eventCart]="eventCart"
        [widgetType]="widgetType"
        [userState]="userState"
        [coupon]="coupon"
        [finalCouponDiscount]="finalCouponDiscount"
        [addonsPrice]="customFieldsAddonsPrice"
        (paypalSuccessfulEvent)="onPaypalSuccessfulEvent($event)"
        (resetPaypalEvent)="onResetPaypal($event)"
        (paypalOrderStartedEvent)="paypalOrderStartedEvent.emit()"
        (paypalCancelledEvent)="paypalCancelledEvent.emit()"
      />
    </div>
  }

  @if (isSaferpayEnabled && paymentType === paymentTypes.SAFERPAY && price > 0 && isFormValid) {
    <!-- app-cw-saferpay renders nothing (it only drives the redirect), so this wrapper
         stays unpadded in the card layout - a padded one shows as an empty strip. -->
    <div [class.mt-4]="!useCardLayout">
      <app-cw-saferpay
        #cwSaferpay
        [price]="price"
        [partner]="partner"
        [cart]="cart"
        [eventCart]="eventCart"
        [widgetType]="widgetType"
        [appointmentServicesAvailable]="appointmentServicesAvailable"
        [couponCode]="coupon?.code"
        [addonsPrice]="customFieldsAddonsPrice"
        [originalAmount]="totalAmount"
        (saferpaySuccessfulEvent)="onSaferpaySuccessfulEvent($event)"
        (resetSaferpayEvent)="onResetSaferpay($event)"
      />
    </div>
  }

  @if (isAdyenSelectable && paymentType === paymentTypes.ADYEN && price > 0 && isFormValid && (!reservationExpired() || adyenPaymentInFlight())) {
    <div class="cw-stripe-box" [class.cw-payment-method-card-body]="useCardLayout" [class.mt-4]="!useCardLayout">
      <div class="stripe-field">
      <app-cw-adyen
        [hostDrivesSubmit]="hostDrivesSubmit"
        [resumeSnapshot]="adyenResumeSnapshot"
        [price]="price"
        [partner]="partner"
        [cart]="cart"
        [eventCart]="eventCart"
        [widgetType]="widgetType"
        [appointmentServicesAvailable]="appointmentServicesAvailable"
        [storeId]="storeId"
        [bookingLinkUuid]="bookingLinkUuid"
        [predefinedSlotUuid]="predefinedSlotUuid"
        [isCombiServiceSelected]="isCombiServiceSelected"
        [shopperEmail]="userState?.eMail"
        [lang]="lang"
        (adyenSuccessfulEvent)="onAdyenSuccessfulEvent($event)"
        (resetAdyenEvent)="onAdyenReset($event)"
        (adyenPaymentStartedEvent)="onAdyenPaymentStarted()"
      />
      </div>
    </div>
  }
</ng-template>
